import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PromoBanner from "../../components/PromoBanner";
import {
  Flame, Clock, TrendingUp, Trophy, MessageSquare, Eye,
  ArrowBigUp, ArrowBigDown, Sparkles, Plus, Users, Hash,
} from "lucide-react";
import { toast } from "sonner";

const SORTS = [
  { id: "hot", label: "Hot", icon: Flame },
  { id: "new", label: "New", icon: Clock },
  { id: "top_today", label: "Top Today", icon: Trophy },
  { id: "top_week", label: "Top Week", icon: Trophy },
  { id: "rising", label: "Rising", icon: TrendingUp },
];

function timeAgo(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d`;
  return d.toLocaleDateString();
}

function PostCard({ post, onVote }) {
  const [busy, setBusy] = useState(false);
  const [localVote, setLocalVote] = useState(0);
  const [localScore, setLocalScore] = useState(post.score || 0);

  const vote = async (dir) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/api/community/posts/${post.id}/vote`, {
        direction: dir === localVote ? 0 : dir,
      });
      setLocalVote(data.your_vote);
      setLocalScore(data.score);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vote failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      className={`card-surface p-4 flex gap-3 hover:border-white/20 transition-colors ${
        post.reduced_visibility || post.mod_status === "reduced_visibility"
          ? "opacity-70 border-[#ffd166]/30"
          : ""
      }`}
      data-testid={`post-card-${post.id}`}
    >
      <div className="flex flex-col items-center gap-1 pt-1">
        <button onClick={() => vote(1)} aria-label="Upvote"
                className={`p-1 rounded transition-colors ${
                  localVote === 1 ? "text-[var(--bull)]" : "text-white/40 hover:text-[var(--bull)]"
                }`}
                data-testid={`vote-up-${post.id}`}>
          <ArrowBigUp className="w-5 h-5" fill={localVote === 1 ? "currentColor" : "none"} />
        </button>
        <div className="font-mono text-[13px] tabular-nums font-semibold text-white/85"
             data-testid={`score-${post.id}`}>{localScore}</div>
        <button onClick={() => vote(-1)} aria-label="Downvote"
                className={`p-1 rounded transition-colors ${
                  localVote === -1 ? "text-[var(--bear)]" : "text-white/40 hover:text-[var(--bear)]"
                }`}
                data-testid={`vote-down-${post.id}`}>
          <ArrowBigDown className="w-5 h-5" fill={localVote === -1 ? "currentColor" : "none"} />
        </button>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap text-[11px] label-mono">
          {post.community_slug && (
            <Link to={`/community/${post.community_slug}`}
                  className="text-white/75 hover:text-white font-semibold"
                  data-testid={`community-link-${post.community_slug}`}>
              r/{post.community_slug}
            </Link>
          )}
          <span className="text-white/30">·</span>
          <span>Posted by</span>
          <Link to={`/community/u/${post.author_username}`}
                className="text-white/75 hover:text-white flex items-center gap-1"
                data-testid={`user-link-${post.author_username}`}>
            {post.author_avatar_url && (
              <img src={post.author_avatar_url} alt="" className="w-4 h-4 rounded-full" loading="lazy" />
            )}
            @{post.author_username}
          </Link>
          <span className="text-white/30">·</span>
          <span>{timeAgo(post.created_at)}</span>
          {(post.ticker_tags || []).slice(0, 4).map((t) => (
            <span key={`tag-${post.id}-${t}`}
                  className="px-1.5 py-0.5 rounded bg-[var(--bull)]/15 text-[var(--bull)] font-mono text-[10px]">
              ${t}
            </span>
          ))}
        </div>
        <Link to={`/community/post/${post.id}`}
              className="block mt-1.5 text-white text-[16px] font-semibold tracking-tight leading-snug hover:text-white/85"
              data-testid={`post-title-${post.id}`}>
          {post.title}
        </Link>
        {post.body && (
          <p className="mt-1.5 text-white/70 text-[13px] leading-relaxed line-clamp-3 whitespace-pre-wrap">
            {post.body}
          </p>
        )}
        <div className="mt-2.5 flex items-center gap-4 text-[11px] label-mono">
          <span className="inline-flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> {post.comment_count || 0}
          </span>
          <span className="inline-flex items-center gap-1">
            <Eye className="w-3 h-3" /> {post.view_count || 0}
          </span>
          {post.mod_status === "reduced_visibility" && (
            <span className="text-[#ffd166] inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Reduced visibility
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function HotToday() {
  const [data, setData] = useState({ top_posts: [], top_tickers: [] });
  useEffect(() => {
    api.get("/api/community/feed/hot-today").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  return (
    <div className="card-surface p-4" data-testid="hot-today">
      <div className="flex items-center gap-2 mb-3">
        <Flame className="w-4 h-4 text-[#ff6b35]" />
        <div className="text-white text-[14px] font-semibold tracking-tight">What's Hot Today</div>
      </div>
      {data.top_tickers?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {data.top_tickers.slice(0, 8).map((t) => (
            <span key={`hot-t-${t}`}
                  className="px-2 py-0.5 rounded-full bg-[var(--bull)]/15 border border-[var(--bull)]/25 text-[var(--bull)] font-mono text-[11px]">
              ${t}
            </span>
          ))}
        </div>
      )}
      <div className="space-y-1.5">
        {(data.top_posts || []).slice(0, 6).map((p) => (
          <Link key={p.id} to={`/community/post/${p.id}`}
                className="block text-[12px] text-white/80 hover:text-white leading-snug"
                data-testid={`hot-post-${p.id}`}>
            <span className="text-white/45 mr-1.5">↑{p.score || 0}</span>
            {p.title}
          </Link>
        ))}
      </div>
    </div>
  );
}

function CommunitiesSidebar({ selectedSlug }) {
  const [communities, setCommunities] = useState([]);
  useEffect(() => {
    api.get("/api/community/communities").then(({ data }) => setCommunities(data.communities || [])).catch(() => {});
  }, []);
  return (
    <div className="card-surface p-3" data-testid="communities-sidebar">
      <div className="flex items-center gap-2 mb-2 px-1">
        <Users className="w-3.5 h-3.5 text-white/60" />
        <div className="text-white text-[13px] font-semibold tracking-tight">Communities</div>
      </div>
      <div className="max-h-[520px] overflow-y-auto space-y-0.5 pr-1">
        {communities.map((c) => (
          <Link key={c.id} to={`/community/${c.slug}`}
                className={`block px-2 py-1.5 rounded-lg text-[12px] transition-colors ${
                  selectedSlug === c.slug
                    ? "bg-white/[0.06] text-white"
                    : "text-white/70 hover:bg-white/[0.03] hover:text-white"
                }`}
                data-testid={`sidebar-community-${c.slug}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="truncate">{c.name}</span>
              <span className="label-mono">{c.member_count || 0}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function CommunityHome({ params }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const slug = params?.slug || null;
  const [sort, setSort] = useState("hot");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [community, setCommunity] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const endpoint = slug
        ? `/api/community/communities/${slug}/posts?sort=${sort}&limit=30`
        : `/api/community/feed?sort=${sort}&limit=30`;
      const { data } = await api.get(endpoint);
      setPosts(data.posts || []);
      if (slug) {
        const { data: com } = await api.get(`/api/community/communities/${slug}`);
        setCommunity(com);
      } else {
        setCommunity(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load feed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort, slug]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="community-page">
      <Header />
      <PromoBanner />
      <main id="main-content" className="flex-1 max-w-[1400px] w-full mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
          <section className="min-w-0">
            {/* Community header (if inside a community) */}
            {community && (
              <div className="card-surface p-5 mb-3 relative overflow-hidden" data-testid={`community-header-${community.slug}`}>
                <div className="absolute inset-0 pointer-events-none opacity-40"
                     style={{ background: "radial-gradient(circle at 100% 0%, rgba(0,255,156,0.14), transparent 60%)" }} />
                <div className="relative">
                  <div className="label-mono">r/{community.slug}</div>
                  <h1 className="text-2xl sm:text-3xl text-white font-semibold tracking-tight mt-1">{community.name}</h1>
                  <p className="text-white/65 text-[13px] mt-1.5 max-w-2xl">{community.description}</p>
                  <div className="mt-3 flex items-center gap-3 label-mono">
                    <span><Users className="inline w-3 h-3 mr-1" />{community.member_count || 0} members</span>
                    <span>·</span>
                    <span>{community.post_count || 0} posts</span>
                  </div>
                </div>
              </div>
            )}
            {!community && (
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="label-mono">Community</div>
                  <h1 className="text-2xl sm:text-3xl text-white font-semibold tracking-tight">Home feed</h1>
                </div>
                {user && (
                  <button onClick={() => navigate("/community/new")}
                          className="btn-accent inline-flex items-center gap-2"
                          data-testid="create-post-btn">
                    <Plus className="w-3.5 h-3.5" /> New post
                  </button>
                )}
              </div>
            )}

            {/* Sort tabs */}
            <div className="flex items-center gap-1 flex-wrap mb-3">
              {SORTS.map((s) => (
                <button key={s.id} onClick={() => setSort(s.id)}
                        className={`px-3 py-1.5 rounded-full text-[12px] font-mono uppercase tracking-wider border transition-colors ${
                          sort === s.id
                            ? "bg-white/[0.08] border-white/30 text-white"
                            : "bg-white/[0.02] border-white/10 text-white/60 hover:text-white hover:border-white/25"
                        }`}
                        data-testid={`sort-${s.id}`}>
                  <s.icon className="inline w-3 h-3 mr-1" />
                  {s.label}
                </button>
              ))}
              {community && user && (
                <button onClick={() => navigate(`/community/new?community=${community.slug}`)}
                        className="ml-auto btn-accent inline-flex items-center gap-2"
                        data-testid="create-post-in-community-btn">
                  <Plus className="w-3.5 h-3.5" /> New post
                </button>
              )}
            </div>

            {/* Posts */}
            <div className="space-y-2">
              {loading && <div className="text-white/40 py-6 text-center text-sm">Loading feed…</div>}
              {!loading && posts.length === 0 && (
                <div className="text-white/40 py-6 text-center text-sm">
                  No posts yet — be the first to post!
                </div>
              )}
              {posts.map((p) => <PostCard key={p.id} post={p} />)}
            </div>
          </section>

          {/* Sidebar */}
          <aside className="space-y-3 lg:sticky lg:top-[80px] lg:self-start">
            <HotToday />
            <CommunitiesSidebar selectedSlug={slug} />
            <Link to="/community/feature-requests"
                  className="card-surface p-4 block hover:border-white/20 transition-colors"
                  data-testid="link-feature-requests">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[var(--bull)]" />
                <div className="text-white text-[13px] font-semibold tracking-tight">Feature Requests</div>
              </div>
              <div className="text-white/55 text-[12px] mt-1">Suggest and vote on FlowFinder features →</div>
            </Link>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}
